import { useContext, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { CartContext } from "../../store/context/CartContext";
import { useStream } from "../../hooks/useStream";
import StateChip from "./StateChip";
import ChannelInfoPanel from "./ChannelInfoPanel";
import MetricsPanel from "./MetricsPanel";

const Stream = () => {
  const { setTitleSeccion } = useContext(CartContext);

  useEffect(() => {
    setTitleSeccion("Stream");
  }, [setTitleSeccion]);

  const {
    channel,
    metrics,
    loading,
    actionLoader,
    canStart,
    canStop,
    isTransitory,
    lastRefreshedAt,
    confirmStopOpen,
    setConfirmStopOpen,
    refresh,
    onStart,
    onStop,
    mp4Loading,
    mp4Status,
    mp4ProgressPct,
    mp4Downloading,
    mp4InProgress,
    mp4Ready,
    onGenerateMp4,
    onDownloadMp4,
  } = useStream();

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const startTooltip = !canStart
    ? channel?.state === "RUNNING"
      ? "El canal ya esta en ejecucion"
      : "No disponible en este estado"
    : "";

  const stopTooltip = !canStop
    ? channel?.state === "IDLE"
      ? "El canal ya esta detenido"
      : "No disponible en este estado"
    : "";

  const mp4Label = mp4InProgress
    ? `Generando MP4... (${mp4Status}${
        mp4ProgressPct != null ? ` ${mp4ProgressPct}%` : ""
      })`
    : mp4Ready
    ? mp4Downloading
      ? "Descargando..."
      : "Descargar MP4"
    : mp4Status === "ERROR"
    ? "Reintentar generacion MP4"
    : mp4Status === "CANCELED"
    ? "Generacion cancelada - Reintentar"
    : "Descargar MP4 del ultimo live";

  const mp4Disabled = mp4Loading || mp4InProgress || mp4Downloading;
  const mp4ShowSpinner = mp4Loading || mp4InProgress || mp4Downloading;

  const mp4Tooltip = mp4InProgress
    ? "La conversion puede tardar varios minutos"
    : mp4Ready
    ? "El MP4 esta listo, hace click para descargar"
    : "";

  const onMp4Click = mp4Ready ? onDownloadMp4 : onGenerateMp4;

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ flexWrap: "wrap" }}
      >
        <StateChip state={channel?.state} />
        <Typography variant="h6">
          Canal #{channel?.channelId || channel?.id || "-"}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Ultima actualizacion:{" "}
          {lastRefreshedAt
            ? new Date(lastRefreshedAt).toLocaleTimeString()
            : "-"}
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={refresh}
          size="small"
          variant="outlined"
        >
          Refrescar
        </Button>
      </Stack>

      {isTransitory && (
        <Alert severity="info">
          Canal en transicion ({channel?.state})... esto puede tardar 60-90
          segundos.
        </Alert>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Paper sx={{ p: 3, minWidth: 260 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Acciones
          </Typography>
          <Stack spacing={1.5}>
            <Tooltip title={startTooltip}>
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={
                    actionLoader ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <PlayArrowIcon />
                    )
                  }
                  disabled={!canStart}
                  onClick={onStart}
                >
                  Iniciar
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={stopTooltip}>
              <span>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={
                    actionLoader ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <StopIcon />
                    )
                  }
                  disabled={!canStop}
                  onClick={() => setConfirmStopOpen(true)}
                >
                  Detener
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={mp4Tooltip}>
              <span>
                <Button
                  fullWidth
                  variant={mp4Ready ? "contained" : "outlined"}
                  color={mp4Ready ? "success" : "secondary"}
                  startIcon={
                    mp4ShowSpinner ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={mp4Disabled}
                  onClick={onMp4Click}
                >
                  {mp4Label}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Paper>

        <ChannelInfoPanel channel={channel} sx={{ flex: 1 }} />
      </Stack>

      <MetricsPanel metrics={metrics} channelState={channel?.state} />

      <Dialog
        open={confirmStopOpen}
        onClose={() => setConfirmStopOpen(false)}
      >
        <DialogTitle>Detener transmision</DialogTitle>
        <DialogContent>
          <Typography>
            Esto corta el vivo inmediatamente. Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStopOpen(false)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={onStop}
            disabled={actionLoader}
          >
            {actionLoader ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Detener"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Stream;
